import { Phone, MessageCircle, Menu, X, Facebook, Instagram, Plus, Minus } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_compliance-pro-24/artifacts/c65ii9su_WINTRUST%20LOGO.png";
const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20consultation%20for%20tax%20services%20in%20Coimbatore.%20Please%20assist%20me";
const PHONE_URL = "tel:+918940988776";
const FACEBOOK_URL = "https://www.facebook.com/share/1KXgzbQ4zP/";
const INSTAGRAM_URL = "https://www.instagram.com/wintrustsolutions";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [calculatorsOpen, setCalculatorsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null); // 'services' | 'calculators' | 'resources' | null
  const location = useLocation();

  const toggleMobileSection = (section) => {
    setMobileSection((prev) => (prev === section ? null : section));
  };

  const services = [
    { label: "GST Registration", href: "/gst-registration" },
    { label: "GST Return Filing", href: "/gst-return-filing" },
    { label: "E-Commerce GST Registration", href: "/ecom-gst-registration" },
    { label: "E-Commerce GST Filing", href: "/ecom-gst-filing" },
    { label: "ITR Filing", href: "/itr-filing" },
    { label: "TDS Filing", href: "/tds-filing" },
    { label: "Business Registration", href: "/business-registration" },
    { label: "Business Loan Assistance", href: "/business-loan-assistance" },
    { label: "Personal Loan Assistance", href: "/personal-loan-assistance" },
    { label: "Health Insurance", href: "/health-insurance-advisory" },
  ];

  const calculators = [
    { label: "GST Late Fee & Penalty Calculator", href: "/gst-penalty-calculator" },
    { label: "GST Interest Calculator", href: "/gst-interest-calculator" },
    { label: "Income Tax Calculator", href: "/income-tax-calculator" },
    { label: "TDS Penalty Calculator", href: "/tds-penalty-calculator" },
    { label: "Business Loan Eligibility Calculator", href: "/loan-eligibility-calculator" },
    { label: "EMI Calculator", href: "/emi-calculator" },
  ];

  const resourceGroups = [
    {
      category: "Tools",
      items: [
        { label: "Business Compliance Roadmap", href: "/compliance-roadmap" },
      ],
    },
    {
      category: "Templates",
      items: [
        { label: "GST Notice Response Template", href: "/gst-notice-response-template" },
        { label: "GST Reconciliation Worksheet", href: "/gst-reconciliation-worksheet" },
        { label: "ITR Checklist", href: "/itr-checklist" },
      ],
    },
  ];

  return (
    <header className="bg-white sticky top-0 z-50 border-b border-slate-100" data-testid="navigation">
      <nav className="container-custom" aria-label="Main navigation">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src={LOGO_URL}
              alt="WINTRUST SOLUTIONS - Tax Consultant in Coimbatore"
              className="h-14 w-auto"
              width="56"
              height="56"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <Link
              to="/"
              className={`font-medium transition-colors ${location.pathname === "/" ? "text-gold" : "text-navy hover:text-gold"}`}
              data-testid="nav-home"
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className="font-medium text-navy hover:text-gold transition-colors flex items-center gap-1"
                data-testid="nav-services-btn"
              >
                Services
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                className={`absolute top-full left-0 w-60 bg-white shadow-lg rounded-lg py-2 border border-slate-100 transition-all ${servicesOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
                data-testid="nav-services-dropdown"
              >
                {services.map((service) => (
                  <Link
                    key={service.href}
                    to={service.href}
                    className="block px-4 py-2 text-navy hover:bg-slate-50 hover:text-gold transition-colors text-sm"
                  >
                    {service.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Calculators Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setCalculatorsOpen(true)}
              onMouseLeave={() => setCalculatorsOpen(false)}
            >
              <button
                className="font-medium text-navy hover:text-gold transition-colors flex items-center gap-1"
                data-testid="nav-calculators-btn"
              >
                Calculators
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                className={`absolute top-full left-0 w-72 bg-white shadow-lg rounded-lg py-2 border border-slate-100 transition-all ${calculatorsOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
                data-testid="nav-calculators-dropdown"
              >
                {calculators.map((calc) => (
                  <Link
                    key={calc.href}
                    to={calc.href}
                    className="block px-4 py-2 text-navy hover:bg-slate-50 hover:text-gold transition-colors text-sm"
                    data-testid={`nav-calc-link-${calc.href.slice(1)}`}
                  >
                    {calc.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Resources Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setResourcesOpen(true)}
              onMouseLeave={() => setResourcesOpen(false)}
            >
              <button
                className="font-medium text-navy hover:text-gold transition-colors flex items-center gap-1"
                data-testid="nav-resources-btn"
              >
                Resources
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                className={`absolute top-full left-0 w-72 bg-white shadow-lg rounded-lg py-3 border border-slate-100 transition-all ${resourcesOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
                data-testid="nav-resources-dropdown"
              >
                {resourceGroups.map((group, idx) => (
                  <div key={group.category} className={idx > 0 ? "mt-2 pt-2 border-t border-slate-100" : ""}>
                    <p className="px-4 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      {group.category}
                    </p>
                    {group.items.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="block px-4 py-2 text-navy hover:bg-slate-50 hover:text-gold transition-colors text-sm"
                        data-testid={`nav-resource-link-${item.href.slice(1)}`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <Link
              to="/blog"
              className={`font-medium transition-colors ${location.pathname.startsWith("/blog") ? "text-gold" : "text-navy hover:text-gold"}`}
              data-testid="nav-blog"
            >
              Blog
            </Link>

            <a href="/#contact" className="font-medium text-navy hover:text-gold transition-colors" data-testid="nav-contact">
              Contact
            </a>

            {/* Social Icons */}
            <div className="flex items-center gap-2">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-navy/5 flex items-center justify-center text-navy hover:bg-gold hover:text-white transition-all"
                aria-label="Follow us on Facebook"
                data-testid="nav-facebook"
              >
                <Facebook size={16} />
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-navy/5 flex items-center justify-center text-navy hover:bg-gold hover:text-white transition-all"
                aria-label="Follow us on Instagram"
                data-testid="nav-instagram"
              >
                <Instagram size={16} />
              </a>
            </div>

            {/* WhatsApp Header Button */}
            <a
              id="whatsapp-header"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-location="header"
              className="btn-whatsapp"
            >
              <MessageCircle size={18} />
              WhatsApp
            </a>

            {/* Phone Header Button */}
            <a
              id="phone-header"
              href={PHONE_URL}
              data-location="header"
              className="btn-primary flex items-center gap-2"
            >
              <Phone size={18} />
              Call Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-navy"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden pt-2 pb-24 border-t border-slate-100" data-testid="mobile-nav">
            <Link
              to="/"
              className="block py-3 text-navy font-semibold hover:text-gold border-b border-slate-100"
              onClick={() => setIsOpen(false)}
              data-testid="mobile-home-link"
            >
              Home
            </Link>

            {/* Services (collapsible) */}
            <div className="border-b border-slate-100" data-testid="mobile-services-section">
              <button
                type="button"
                onClick={() => toggleMobileSection("services")}
                className="w-full flex items-center justify-between py-3 text-navy font-semibold hover:text-gold"
                aria-expanded={mobileSection === "services"}
                data-testid="mobile-services-toggle"
              >
                <span>Services</span>
                {mobileSection === "services" ? <Minus size={18} className="text-gold" /> : <Plus size={18} className="text-slate-400" />}
              </button>
              {mobileSection === "services" && (
                <div className="pl-4 pb-3 space-y-2" data-testid="mobile-services-items">
                  {services.map((service) => (
                    <Link
                      key={service.href}
                      to={service.href}
                      className="block py-1.5 text-slate-600 hover:text-gold text-sm"
                      onClick={() => setIsOpen(false)}
                    >
                      {service.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Calculators (collapsible) */}
            <div className="border-b border-slate-100" data-testid="mobile-calculators-section">
              <button
                type="button"
                onClick={() => toggleMobileSection("calculators")}
                className="w-full flex items-center justify-between py-3 text-navy font-semibold hover:text-gold"
                aria-expanded={mobileSection === "calculators"}
                data-testid="mobile-calculators-toggle"
              >
                <span>Calculators</span>
                {mobileSection === "calculators" ? <Minus size={18} className="text-gold" /> : <Plus size={18} className="text-slate-400" />}
              </button>
              {mobileSection === "calculators" && (
                <div className="pl-4 pb-3 space-y-2" data-testid="mobile-calculators-items">
                  {calculators.map((calc) => (
                    <Link
                      key={calc.href}
                      to={calc.href}
                      className="block py-1.5 text-slate-600 hover:text-gold text-sm"
                      onClick={() => setIsOpen(false)}
                    >
                      {calc.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Resources (collapsible) */}
            <div className="border-b border-slate-100" data-testid="mobile-resources-section">
              <button
                type="button"
                onClick={() => toggleMobileSection("resources")}
                className="w-full flex items-center justify-between py-3 text-navy font-semibold hover:text-gold"
                aria-expanded={mobileSection === "resources"}
                data-testid="mobile-resources-toggle"
              >
                <span>Resources</span>
                {mobileSection === "resources" ? <Minus size={18} className="text-gold" /> : <Plus size={18} className="text-slate-400" />}
              </button>
              {mobileSection === "resources" && (
                <div className="pl-4 pb-3 space-y-3" data-testid="mobile-resources-items">
                  {resourceGroups.map((group) => (
                    <div key={group.category}>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                        {group.category}
                      </p>
                      <div className="space-y-1.5">
                        {group.items.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            className="block py-1 text-slate-600 hover:text-gold text-sm"
                            onClick={() => setIsOpen(false)}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/blog"
              className="block py-3 text-navy font-semibold hover:text-gold border-b border-slate-100"
              onClick={() => setIsOpen(false)}
              data-testid="mobile-blog-link"
            >
              Blog
            </Link>

            <a
              href="/#contact"
              className="block py-3 text-navy font-semibold hover:text-gold border-b border-slate-100"
              onClick={() => setIsOpen(false)}
              data-testid="mobile-contact-link"
            >
              Contact
            </a>

            {/* Social icons */}
            <div className="flex gap-3 mt-5">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-navy/10 flex items-center justify-center text-navy hover:bg-gold hover:text-white transition-all"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-navy/10 flex items-center justify-center text-navy hover:bg-gold hover:text-white transition-all"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>
        )}

        {/* Fixed Bottom Call + WhatsApp bar (mobile only, when menu open) */}
        {isOpen && (
          <div
            className="lg:hidden fixed bottom-0 left-0 right-0 z-[60] bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3 flex gap-3"
            data-testid="mobile-bottom-bar"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp flex-1 justify-center"
              data-testid="mobile-bottom-whatsapp"
            >
              <MessageCircle size={18} />
              WhatsApp
            </a>
            <a
              href={PHONE_URL}
              className="btn-primary flex-1 text-center flex items-center justify-center gap-2"
              data-testid="mobile-bottom-call"
            >
              <Phone size={18} />
              Call
            </a>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navigation;
